<script>
import Breadcrumbs from '@/components/file-browser/Breadcrumbs';
import FileList from '@/components/file-list/FileList';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'DirectoryBrowser',
  components: {
    Breadcrumbs,
    FileList
  },
  props: {
    basePath: {
      type: String,
      default: ''
    },
    fetchMethod: Function,
    site: Object,
    type: {
      default: 'inline',
      validator(type) {
        return ['inline', 'dialog', 'popover'].includes(type);
      }
    }
  },
  data() {
    return {
      currentPath: '',
      errorMessage: '',
      files: [],
      fileSelection: [],
      isLoading: false
    };
  },
  methods: {
    open(options) {
      this.errorMessage = '';
      this.files = [];
      this.isOpen = true;
      this.$emit('open');

      // Fetch files
      this.getFiles(options.path);
    },

    close() {
      this.isOpen = false;
      this.$emit('close');
    },

    getFiles(path) {
      // Make sure the path is under the base path
      this.currentPath = this.isUnderBasePath(path) ? path : this.basePath;
      this.isLoading = true;
      this.$emit('path-change', this.currentPath);

      // You can fetch files using a custom fetch method or using the site API
      const fetchMethod = this.fetchMethod
        ? this.fetchMethod
        : () => {
            return this.$http
              .get(`/sites/${this.site.id}/files`, {
                params: { path: this.currentPath }
              })
              .then(res => (this.files = res.data.files));
          };

      fetchMethod(this.currentPath)
        .then(files => {
          this.files = files
            .map(file => Object.assign(file, { isDisabled: file.type !== 'directory' }))
            .filter(file => {
              // Don't show the parent directory if we're at the base path
              return this.path === this.basePath ? file.name !== '..' : true;
            });
        })
        .catch(err => (this.errorMessage = getErrorMessage(err.response.data.code)))
        .finally(() => (this.isLoading = false));
    },

    handleSelect() {
      this.$emit('select', this.currentPath);
      this.close();
    },

    // Tells if a path is inside of of the site's home folder
    isUnderBasePath(path) {
      path = path || '';
      return path.substring(0, this.basePath.length) === this.basePath;
    }
  }
};
</script>

<template>
  <div
    :class="{
      'file-browser': true,
      'is-inline': type === 'inline',
      'is-dialog': type ==='dialog',
      'is-popover': type === 'popover'
    }"
  >
    <!-- Breadcrumbs -->
    <breadcrumbs
      :path="currentPath"
      :base-path="basePath"
      class="breadcrumbs"
      @path-change="getFiles"
    />

    <!-- Files -->
    <file-list
      ref="fileList"
      v-model="files"
      :can-select="() => false"
      :empty-text="$t('No Files')"
      :error-text="errorMessage"
      :has-error="errorMessage.length > 0"
      :loading="isLoading"
      :filter-method="file => !isUnderBasePath(file.path) && file.path !== '..'"
      @dblclick="handleSelect"
      @path-change="getFiles"
    />

    <div class="file-options">
      <el-button v-if="!errorMessage" type="primary" @click="handleSelect">
        {{ $t('Select') }}
      </el-button>

      <el-button v-if="errorMessage" type="primary" @click="close">
        {{ $t('Done') }}
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.file-browser {
  position: relative;
  font-size: 1rem;

  &.is-dialog {
    padding: 0;
    margin: 0;
  }

  &.is-inline {
    margin-bottom: 1.5rem;
  }

  &.is-popover {
    width: 380px;
    padding: 0 0.5rem 0.5rem 0.5rem;

    @include breakpoint('sm-and-down') {
      width: 360px;
    }
  }
}

.file-options {
  text-align: right;
}
</style>
